import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface Profile {
  id: string;
  username: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  is_premium: boolean;
  premium_tier: string | null;
}

const DEFAULT_PROFILE: Omit<Profile, 'id'> = {
  username: null,
  avatar_url: null,
  is_admin: false,
  is_premium: false,
  premium_tier: null,
};

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch profile and admin role in parallel for security
      const [profileRes, adminRoleRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, avatar_url, is_admin, is_premium, premium_tier, tour_completed')
          .eq('id', user.id)
          .maybeSingle(),
        supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }),
      ]);

      const { data, error: fetchError } = profileRes;
      // Use server-side role check (security definer) as source of truth
      const isAdminFromRole = adminRoleRes.data === true;

      if (fetchError) {
        console.error('Error fetching profile:', fetchError);
        setError(fetchError.message);
        setProfile({
          id: user.id,
          ...DEFAULT_PROFILE,
          is_admin: isAdminFromRole,
        });
      } else if (data) {
        setProfile({
          ...data,
          is_admin: isAdminFromRole || (data.is_admin ?? false),
          is_premium: data.is_premium ?? false,
        });
      } else {
        // Profile doesn't exist - create it with null username (user will set it)
        const newProfile = {
          id: user.id,
          username: null,
          is_premium: false,
          premium_tier: null,
          is_admin: false,
        };

        const { error: insertError } = await supabase
          .from('profiles')
          .insert(newProfile);

        if (insertError && !insertError.message.includes('duplicate')) {
          console.error('Error creating profile:', insertError);
          setError(insertError.message);
        }

        setProfile({
          ...newProfile,
          avatar_url: null,
        });
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred');
      setProfile({
        id: user.id,
        ...DEFAULT_PROFILE,
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const refreshProfile = useCallback(() => {
    return fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    refreshProfile,
    isAdmin: profile?.is_admin ?? false,
    isPremium: profile?.is_premium ?? false,
    premiumTier: profile?.premium_tier ?? null,
    username: profile?.username ?? null,
  };
};

export default useProfile;
